import React, { useEffect, useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { contentApi, appApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

export const StepQuestionnaire = ({ application, reload, goNext, goBack }) => {
  const [content, setContent] = useState(null);
  const [answers, setAnswers] = useState(application.questionnaire || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { contentApi.questionnaire().then(({ data }) => setContent(data)); }, []);

  const setAnswer = (qid, patch) => setAnswers((a) => ({ ...a, [qid]: { ...(a[qid] || {}), ...patch } }));

  const answeredCount = (section) => section.questions.filter((q) => answers[q.id]?.value).length;

  const save = async (advance) => {
    setSaving(true);
    try {
      await appApi.saveQuestionnaire(application.id, answers);
      toast.success("Questionnaire saved");
      await reload();
      if (advance) goNext();
    } catch (err) {
      toast.error("Could not save questionnaire");
    } finally { setSaving(false); }
  };

  if (!content) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-wgreen-600" /></div>;

  const scope = application.selected_categories && application.selected_categories.length ? application.selected_categories : null;
  const sections = scope ? content.sections.filter((s) => scope.includes(s.key)) : content.sections;

  return (
    <div>
      <h2 className="font-heading text-lg font-semibold text-navy-900">Questionnaire</h2>
      <p className="mb-6 text-sm text-[var(--wechr-muted-text)]">Answer each question and add notes describing your controls. Evidence examples are provided as hints.{scope ? " Only your selected certification categories are shown." : ""}</p>
      <Accordion type="multiple" defaultValue={sections[0] ? [sections[0].key] : []} className="space-y-3">
        {sections.map((section) => (
          <AccordionItem key={section.key} value={section.key} className="rounded-xl border border-[var(--wechr-border)] px-4" data-testid={`questionnaire-topic-${section.key}`}>
            <AccordionTrigger className="text-left font-heading text-base font-semibold text-navy-900 hover:no-underline">
              <span className="flex items-center gap-2">{section.title}
                <span className="rounded-full bg-[var(--wechr-subtle)] px-2 py-0.5 text-xs font-medium text-navy-700">{answeredCount(section)}/{section.questions.length}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-6 pb-2">
                {section.questions.map((q) => (
                  <div key={q.id} className="rounded-lg border border-[var(--wechr-border)] p-4" data-testid="questionnaire-question">
                    <p className="font-medium text-navy-900">{q.text}</p>
                    <p className="mt-0.5 text-xs text-[var(--wechr-muted-text)]">Evidence examples: {q.evidence_hint}</p>
                    <RadioGroup className="mt-3 grid gap-2 sm:grid-cols-2" value={answers[q.id]?.value || ""} onValueChange={(v) => setAnswer(q.id, { value: v })}>
                      {content.answer_options.map((o) => (
                        <div key={o.value} className="flex items-center gap-2">
                          <RadioGroupItem value={o.value} id={`${q.id}-${o.value}`} data-testid={`questionnaire-answer-${q.id}-${o.value}`} />
                          <Label htmlFor={`${q.id}-${o.value}`} className="cursor-pointer text-sm font-normal">{o.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <Textarea className="mt-3" rows={2} placeholder="Notes (optional)" value={answers[q.id]?.notes || ""} onChange={(e) => setAnswer(q.id, { notes: e.target.value })} />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={goBack} data-testid="application-back-button">Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="application-save-draft-button">Save draft</Button>
          <Button className="btn-green" onClick={() => save(true)} disabled={saving} data-testid="application-next-button">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Save & continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepQuestionnaire;
